import { SIZE_MAX, SIZE_MIN, getFieldOptions } from '@/entities/profile';
import type { NationalityRef } from '@/shared/api';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useTranslate } from '@tolgee/react';
import { useEffect } from 'react';
import { MultiCheckField, RequiredMark, SelectField } from './fields';

/** Legacy step6 (public): practice, origins, languages, appearance. */
export interface PracticeStepData {
  licence?: number;
  smoke?: number;
  faith?: number;
  minaj?: number;
  veil?: number;
  nationality?: number;
  origin?: number;
  studyCountry?: number;
  spokenLanguages: string[];
  lifePersonality: string[];
  shape?: number;
  size?: number;
  eye?: number;
  hair?: number;
}

interface StepPracticeProps {
  data: PracticeStepData;
  gender: number; // 1 = male, 2 = female
  nationalities: NationalityRef[];
  onChange: (data: Partial<PracticeStepData>) => void;
  onValidChange: (isValid: boolean) => void;
}

const isSet = (v: number | undefined) => v !== undefined && v !== null;

export function StepPractice({
  data,
  gender,
  nationalities,
  onChange,
  onValidChange,
}: StepPracticeProps) {
  const { t } = useTranslate();
  const isWoman = gender === 2;
  const nationalityOptions = nationalities.map((n) => ({ value: String(n.id), label: n.label }));

  // Required per legacy NodeHumanInfoPublicType (+ veil for women only);
  // lifePersonality and shape are optional there.
  // biome-ignore lint/correctness/useExhaustiveDependencies: onValidChange is stable from parent
  useEffect(() => {
    onValidChange(
      isSet(data.licence) &&
        isSet(data.smoke) &&
        isSet(data.faith) &&
        isSet(data.minaj) &&
        (!isWoman || isSet(data.veil)) &&
        isSet(data.nationality) &&
        isSet(data.origin) &&
        isSet(data.studyCountry) &&
        data.spokenLanguages.length > 0 &&
        isSet(data.size) &&
        (data.size as number) >= SIZE_MIN &&
        (data.size as number) <= SIZE_MAX &&
        isSet(data.eye) &&
        isSet(data.hair),
    );
  }, [data, isWoman]);

  return (
    <div className="space-y-4">
      <SelectField
        id="faith"
        label={t('profile.fields.faith')}
        options={getFieldOptions(t, 'faith')}
        value={data.faith}
        onChange={(v) => onChange({ faith: v })}
        required
      />
      <SelectField
        id="minaj"
        label={t('profile.fields.minaj')}
        options={getFieldOptions(t, 'minaj')}
        value={data.minaj}
        onChange={(v) => onChange({ minaj: v })}
        required
      />
      {isWoman && (
        <SelectField
          id="veil"
          label={t('profile.fields.veil')}
          options={getFieldOptions(t, 'veil')}
          value={data.veil}
          onChange={(v) => onChange({ veil: v })}
          required
        />
      )}
      <SelectField
        id="licence"
        label={t('profile.fields.licence')}
        options={getFieldOptions(t, 'boolean')}
        value={data.licence}
        onChange={(v) => onChange({ licence: v })}
        required
      />
      <SelectField
        id="smoke"
        label={t('profile.fields.smoke')}
        options={getFieldOptions(t, 'smoke')}
        value={data.smoke}
        onChange={(v) => onChange({ smoke: v })}
        required
      />
      <SelectField
        id="nationality"
        label={t('profile.fields.nationality')}
        options={nationalityOptions}
        value={data.nationality}
        onChange={(v) => onChange({ nationality: v })}
        required
      />
      <SelectField
        id="origin"
        label={t('profile.fields.origin')}
        options={nationalityOptions}
        value={data.origin}
        onChange={(v) => onChange({ origin: v })}
        required
      />
      <SelectField
        id="studyCountry"
        label={t('profile.fields.studyCountry')}
        options={getFieldOptions(t, 'studyCountry')}
        value={data.studyCountry}
        onChange={(v) => onChange({ studyCountry: v })}
        required
      />
      <MultiCheckField
        id="spokenLanguages"
        label={t('profile.fields.spokenLanguages')}
        options={getFieldOptions(t, 'spokenLanguages')}
        values={data.spokenLanguages}
        onChange={(values) => onChange({ spokenLanguages: values })}
        required
        hint={t('signup.hints.languages')}
      />
      <MultiCheckField
        id="lifePersonality"
        label={t('profile.fields.lifePersonality')}
        options={getFieldOptions(t, 'lifePersonality')}
        values={data.lifePersonality}
        onChange={(values) => onChange({ lifePersonality: values })}
      />
      <SelectField
        id="shape"
        label={t('profile.fields.shape')}
        options={getFieldOptions(t, 'shape')}
        value={data.shape}
        onChange={(v) => onChange({ shape: v })}
      />
      <div className="space-y-2">
        <Label htmlFor="size">
          {t('profile.fields.size')} (cm)
          <RequiredMark required />
        </Label>
        <Input
          id="size"
          type="number"
          inputMode="numeric"
          min={SIZE_MIN}
          max={SIZE_MAX}
          placeholder="170"
          value={data.size ?? ''}
          onChange={(e) =>
            onChange({ size: e.target.value ? Number.parseInt(e.target.value, 10) : undefined })
          }
          required
        />
      </div>
      <SelectField
        id="eye"
        label={t('profile.fields.eye')}
        options={getFieldOptions(t, 'eye')}
        value={data.eye}
        onChange={(v) => onChange({ eye: v })}
        required
      />
      <SelectField
        id="hair"
        label={t('profile.fields.hair')}
        options={getFieldOptions(t, 'hair')}
        value={data.hair}
        onChange={(v) => onChange({ hair: v })}
        required
      />
      <p className="text-xs text-muted-foreground">{t('signup.hints.required')}</p>
    </div>
  );
}
